// GitHub-style activity calendar. Hand-rolled SVG so it matches the
// dashboard theme exactly and we avoid pulling in another dep.

import { useEffect, useMemo, useRef } from 'react';
import css from './Heatmap.module.css';

interface DayCount {
  date: string;        // 'YYYY-MM-DD'
  sessions: number;
  sets: number;
}

interface Props {
  data: DayCount[];
  /** Months back from today. `'all'` derives from the earliest row in data. */
  monthsBack?: number | 'all';
}

const CELL = 12;
const GAP = 2;
const MONTH_LABEL_H = 14;
const DOW_LABEL_W = 22;

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function startOfWeek(d: Date): Date {
  // ISO-ish, weeks starting Monday — better for European eyes
  const out = new Date(d);
  const day = (out.getDay() + 6) % 7; // 0 = Mon
  out.setDate(out.getDate() - day);
  out.setHours(0, 0, 0, 0);
  return out;
}

const DOW_LABELS = ['Mon', '', 'Wed', '', 'Fri', '', ''];

export function Heatmap({ data, monthsBack = 12 }: Props) {
  const byDate = useMemo(() => {
    const m = new Map<string, DayCount>();
    for (const d of data) m.set(d.date, d);
    return m;
  }, [data]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(today);
  if (monthsBack === 'all') {
    // Derive from earliest date in data (or fall back to 12 months if empty).
    let earliest: string | null = null;
    for (const d of data) {
      if (earliest == null || d.date < earliest) earliest = d.date;
    }
    if (earliest) {
      const [y, m, d] = earliest.split('-').map(Number);
      if (y && m && d) start.setTime(new Date(y, m - 1, d).getTime());
    } else {
      start.setMonth(start.getMonth() - 12);
    }
  } else {
    start.setMonth(start.getMonth() - monthsBack);
  }
  const firstWeek = startOfWeek(start);

  // Build a column-per-week grid
  const weeks: Date[][] = [];
  for (
    let cursor = new Date(firstWeek);
    cursor <= today;
    cursor.setDate(cursor.getDate() + 7)
  ) {
    const col: Date[] = [];
    for (let d = 0; d < 7; d++) {
      const day = new Date(cursor);
      day.setDate(day.getDate() + d);
      col.push(day);
    }
    weeks.push(col);
  }

  // Intensity buckets — based on set count for the day
  function intensity(day: DayCount | undefined): number {
    if (!day) return 0;
    const sets = day.sets;
    if (sets === 0) return 0;
    if (sets < 5) return 1;
    if (sets < 12) return 2;
    if (sets < 20) return 3;
    return 4;
  }

  const width = DOW_LABEL_W + weeks.length * (CELL + GAP);
  const height = MONTH_LABEL_H + 7 * (CELL + GAP);

  // Month labels: first column index of each month
  const monthLabels: { x: number; label: string }[] = [];
  let prevMonth = -1;
  weeks.forEach((col, i) => {
    const firstDay = col[0]!;
    if (firstDay.getMonth() !== prevMonth) {
      monthLabels.push({
        x: DOW_LABEL_W + i * (CELL + GAP),
        label: firstDay.toLocaleString('en', { month: 'short' }),
      });
      prevMonth = firstDay.getMonth();
    }
  });

  return (
    <HeatmapShell width={width}>
      <svg width={width} height={height} className={css.svg}>
        {monthLabels.map((m, i) => (
          <text
            key={i}
            x={m.x}
            y={MONTH_LABEL_H - 4}
            className={css.monthLabel}
          >
            {m.label}
          </text>
        ))}
        {DOW_LABELS.map(
          (l, i) =>
            l && (
              <text
                key={i}
                x={0}
                y={MONTH_LABEL_H + (i + 1) * (CELL + GAP) - 2}
                className={css.dowLabel}
              >
                {l}
              </text>
            ),
        )}
        {weeks.map((col, weekIdx) =>
          col.map((day, dowIdx) => {
            if (day > today) return null;
            const k = ymd(day);
            const d = byDate.get(k);
            const lvl = intensity(d);
            const title = d
              ? `${k} — ${d.sessions} session${d.sessions === 1 ? '' : 's'}, ${d.sets} working set${d.sets === 1 ? '' : 's'}`
              : `${k} — rest`;
            return (
              <rect
                key={`${weekIdx}-${dowIdx}`}
                x={DOW_LABEL_W + weekIdx * (CELL + GAP)}
                y={MONTH_LABEL_H + dowIdx * (CELL + GAP)}
                width={CELL}
                height={CELL}
                rx={2}
                className={`${css.cell} ${css[`lvl${lvl}`]}`}
              >
                <title>{title}</title>
              </rect>
            );
          }),
        )}
      </svg>
    </HeatmapShell>
  );
}

// Scroll container that auto-scrolls to the right (= today) when the
// content width changes. Lets the heatmap span all of history horizontally
// while landing the user on "now" by default.
function HeatmapShell({ width, children }: { width: number; children: React.ReactNode }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollLeft = el.scrollWidth;
  }, [width]);
  return (
    <div className={css.wrap}>
      <div ref={scrollRef} className={css.scroll}>
        {children}
      </div>
      <div className={css.legend}>
        <span className={css.legendLabel}>less</span>
        {[0, 1, 2, 3, 4].map((l) => (
          <span key={l} className={`${css.legendCell} ${css[`lvl${l}`]}`} />
        ))}
        <span className={css.legendLabel}>more</span>
      </div>
    </div>
  );
}
